/**
 * Review the proposals distilled from a discussion (G8): goal, plan, tasks and the agent's open
 * proposals. Each card shows exactly the change it makes and what was said that led to it
 * (drillable to the conversation); nothing is written until the user accepts, and a card can be
 * edited first. Accepting writes through the encrypted work API (or confirms the agent's
 * proposal through its signed digest).
 */
import { useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import type { BarkRecord, DistillChange, DistillProposal } from '@viko/protocol'
import { Button, Chip } from '@viko/transcript-ui'
import { Check, Pencil, X } from 'lucide-react'
import { useId, useState } from 'react'
import { useBackend } from '../../lib/backend.js'
import type { BarkStore } from '../../lib/bark-store.js'
import { loadGoal, saveGoal } from '../../lib/goals.js'
import { qk } from '../../lib/queries.js'
import { applyDistill, type DistillWriters } from '../../lib/voice/distill.js'
import { errorText, Input, Textarea, useToast } from '../ui.js'

const KIND: Record<DistillChange['op'], string> = {
  'goal.set': 'Goal',
  'plan.brief': 'Plan',
  'task.create': 'New task',
  'task.priority': 'Priority',
  'bark.confirm': 'Agent proposal',
}

function describe(c: DistillChange): string {
  switch (c.op) {
    case 'goal.set':
      return c.goal
    case 'plan.brief':
      return c.brief
    case 'task.create':
      return c.title
    case 'task.priority':
      return `Set priority ${c.priority}`
    case 'bark.confirm':
      return `Confirm the agent's proposal (${c.actions} ${c.actions === 1 ? 'action' : 'actions'})`
  }
}

/** The editable text of a change, and how an edit maps back onto it. */
function editable(c: DistillChange): { value: string; apply(v: string): DistillChange } | null {
  if (c.op === 'goal.set') return { value: c.goal, apply: (goal) => ({ ...c, goal }) }
  if (c.op === 'plan.brief') return { value: c.brief, apply: (brief) => ({ ...c, brief }) }
  if (c.op === 'task.create') return { value: c.title, apply: (title) => ({ ...c, title }) }
  return null
}

export function DistillReview({
  cards,
  bark,
  store,
  onChange,
}: {
  cards: DistillProposal[]
  bark: BarkRecord
  store: BarkStore
  onChange: (next: DistillProposal[]) => void
}) {
  const b = useBackend()
  const qc = useQueryClient()
  const toast = useToast()
  const [busy, setBusy] = useState<string | null>(null)
  const [editing, setEditing] = useState<string | null>(null)
  const [draft, setDraft] = useState('')
  const editId = useId()

  const writers: DistillWriters = {
    // The project's encrypted `goal` entity (milestones kept; optimistic revision check).
    setGoal: async (id, text) => {
      const ents = b.entities
      if (!ents) throw new Error('Unlock this browser to change the goal')
      const current = await loadGoal(ents, id)
      await saveGoal(
        ents,
        id,
        { text, status: current?.status ?? 'active', milestones: current?.milestones ?? [] },
        current,
      )
    },
    updateBrief: (id, brief) => b.api.updateWorkstream(id, { brief }),
    createWorkstream: (i) => b.api.createWorkstream(i),
    createTask: (i) => b.api.createTask(i),
    setPriority: (id, priority) => b.api.updateTask(id, { priority }),
    confirmProposal: async (_barkId, proposalId) => {
      const p = bark.proposals.find((x) => x.id === proposalId && x.status === 'open')
      if (!p) throw new Error('That proposal is no longer open')
      await store.confirm(bark, p)
    },
  }
  const set = (id: string, patch: Partial<DistillProposal>) =>
    onChange(cards.map((c) => (c.id === id ? { ...c, ...patch } : c)))

  const accept = async (c: DistillProposal) => {
    setBusy(c.id)
    try {
      const e = editing === c.id ? editable(c.change) : null
      const card = e ? { ...c, change: e.apply(draft.trim() || e.value) } : c
      await applyDistill(card, writers)
      set(c.id, { change: card.change, status: 'accepted' })
      setEditing(null)
      void qc.invalidateQueries({ queryKey: qk.projects })
      void qc.invalidateQueries({ queryKey: ['goal'] })
      void qc.invalidateQueries({ queryKey: ['voice-goal'] })
      void qc.invalidateQueries({ queryKey: ['tasks'] })
      void qc.invalidateQueries({ queryKey: ['workstreams'] })
      toast(`${KIND[c.change.op]} saved`)
    } catch (err) {
      toast(errorText(err), 'error')
    } finally {
      setBusy(null)
    }
  }

  if (!cards.length)
    return (
      <p className="m-0 text-[14px] text-text-2">
        No outcomes to distill yet. Ask the agent to wrap up with a goal, a plan and tasks.
      </p>
    )
  const open = cards.filter((c) => c.status === 'open').length
  return (
    <section aria-label="Proposals from this discussion" className="grid gap-2">
      <h3 className="m-0 text-[14px] font-semibold text-text-2">
        Proposals · {open} to review of {cards.length}
      </h3>
      <ul className="m-0 grid list-none gap-2 p-0">
        {cards.map((c) => {
          const e = editable(c.change)
          return (
            <li
              key={c.id}
              className="grid gap-2 rounded-xl border border-border-subtle bg-surface-1 p-3"
              data-testid="distill-card"
              data-status={c.status}
            >
              <div className="flex flex-wrap items-center gap-2">
                <Chip>{KIND[c.change.op]}</Chip>
                <span className="text-[15px] font-medium text-text-1">{c.title}</span>
                <span className="flex-1" />
                {c.status !== 'open' ? (
                  <span className="text-[14px] text-text-2">
                    {c.status === 'accepted' ? 'Accepted' : 'Dismissed'}
                  </span>
                ) : null}
              </div>
              {editing === c.id && e ? (
                <div className="grid gap-1">
                  <label htmlFor={`${editId}-${c.id}`} className="text-[14px] text-text-2">
                    Edit before accepting
                  </label>
                  {c.change.op === 'plan.brief' ? (
                    <Textarea
                      id={`${editId}-${c.id}`}
                      value={draft}
                      rows={5}
                      onChange={(ev) => setDraft(ev.target.value)}
                    />
                  ) : (
                    <Input
                      id={`${editId}-${c.id}`}
                      value={draft}
                      onChange={(ev) => setDraft(ev.target.value)}
                    />
                  )}
                </div>
              ) : (
                <p className="m-0 whitespace-pre-wrap text-[14px] text-text-1">
                  {describe(c.change)}
                </p>
              )}
              <details className="text-[14px] text-text-2">
                <summary>Why: what was said ({c.evidence.length})</summary>
                <ul className="m-0 mt-1 grid gap-1 pl-4">
                  {c.evidence.map((ev) => (
                    <li key={ev.messageId + ev.quote}>“{ev.quote}”</li>
                  ))}
                </ul>
                <Link
                  to="/p/$projectId"
                  params={{ projectId: c.projectId }}
                  search={{ i: `bark:${c.barkId}` }}
                  className="text-accent-text"
                >
                  Open the conversation
                </Link>
                {c.research?.length ? (
                  <span>
                    {' '}
                    · {c.research.length} research {c.research.length === 1 ? 'entry' : 'entries'}{' '}
                    cited
                  </span>
                ) : null}
              </details>
              {c.status === 'open' ? (
                <div className="flex flex-wrap gap-2">
                  <Button
                    size="sm"
                    variant="primary"
                    icon={<Check size={14} />}
                    disabled={busy !== null}
                    aria-busy={busy === c.id}
                    onClick={() => void accept(c)}
                  >
                    {editing === c.id ? 'Save and accept' : 'Accept'}
                  </Button>
                  {e && editing !== c.id ? (
                    <Button
                      size="sm"
                      icon={<Pencil size={14} />}
                      onClick={() => {
                        setEditing(c.id)
                        setDraft(e.value)
                      }}
                    >
                      Edit
                    </Button>
                  ) : null}
                  <Button
                    size="sm"
                    variant="ghost"
                    icon={<X size={14} />}
                    onClick={() => set(c.id, { status: 'dismissed' })}
                  >
                    Dismiss
                  </Button>
                </div>
              ) : null}
            </li>
          )
        })}
      </ul>
    </section>
  )
}
